import React, { useCallback, useEffect, useMemo, useState } from "react";

const formatTimeUnit = (value) => value.toString().padStart(2, "0");

// Fixed presale end date so countdown does not restart on refresh or deploy.
const FALLBACK_PRESALE_END_AT = "2026-07-14T14:11:54+05:00";

const getInitialTargetDate = () => {
  const configuredDate =
    process.env.REACT_APP_PRESALE_END_AT || FALLBACK_PRESALE_END_AT;
  const parsedTarget = new Date(configuredDate).getTime();

  if (Number.isNaN(parsedTarget)) {
    return new Date(FALLBACK_PRESALE_END_AT).getTime();
  }

  return parsedTarget;
};

const Hero = () => {
  const targetDate = useMemo(() => getInitialTargetDate(), []);

  const calculateTimeLeft = useCallback(() => {
    const difference = Math.max(targetDate - Date.now(), 0);
    const days = Math.floor(difference / (1000 * 60 * 60 * 24));
    const hours = Math.floor((difference / (1000 * 60 * 60)) % 24);
    const minutes = Math.floor((difference / (1000 * 60)) % 60);
    const seconds = Math.floor((difference / 1000) % 60);

    return { days, hours, minutes, seconds };
  }, [targetDate]);

  const [timeLeft, setTimeLeft] = useState(() => calculateTimeLeft());

  useEffect(() => {
    const timer = setInterval(() => setTimeLeft(calculateTimeLeft()), 1000);
    return () => clearInterval(timer);
  }, [calculateTimeLeft]);

  const countdownUnits = [
    { label: "Days", value: timeLeft.days },
    { label: "Hours", value: timeLeft.hours },
    { label: "Minutes", value: timeLeft.minutes },
    { label: "Seconds", value: timeLeft.seconds },
  ];
  const isExpired =
    timeLeft.days === 0 &&
    timeLeft.hours === 0 &&
    timeLeft.minutes === 0 &&
    timeLeft.seconds === 0;

  return (
    <section className="relative isolate overflow-hidden bg-[#050109]">
      <img
        src="/images/shade.png"
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute right-[-4%] top-1/2 hidden h-[180%] max-h-[200%] w-[56%] -translate-y-1/2 opacity-85 md:block lg:right-[6%%] mt-7"
      />

      {/* 3D Coin for large screens */}
      <div className="pointer-events-none absolute right-[7%] lg:right-[12%] top-[37%] hidden translate-y-[-10%] md:block md:z-10 lg:pr-4px ">
        <div className="coin-3d rotate-y-coin">
          <img
            src="/images/synergy.png"
            alt="SynergyCoin token"
            className="coin-3d__face coin-3d__face--front"
          />
          <img
            src="/images/synergy.png"
            alt="SynergyCoin token reverse"
            className="coin-3d__face coin-3d__face--back"
          />
        </div>
      </div>

      <div className="relative z-10 mx-auto flex min-h-[70vh] w-full max-w-none flex-col items-center gap-6 pb-2 pt-2 sm:min-h-[80vh] sm:gap-8 sm:pb-6 md:max-w-[77%] md:flex-row md:justify-between md:min-h-[calc(100vh-5rem)] md:pt-20 lg:pr-12 lg:pb-10 lg:pt-20">

        {/* 3D Coin for mobile */}
        <div className="relative mt-0 w-full justify-center md:hidden">
          <div className="relative w-full h-[45vh]">
            <img
              src="/images/shade.png"
              alt=""
              aria-hidden="true"
              className="pointer-events-none w-full h-full object-cover transform scale-y-[1] opacity-90"
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="coin-3d rotate-y-coin mx-auto !w-[120px] !h-[120px]">
                <img
                  src="/images/synergy.png"
                  alt="SynergyCoin token"
                  className="coin-3d__face coin-3d__face--front"
                />
                <img
                  src="/images/synergy.png"
                  alt="SynergyCoin token reverse"
                  className="coin-3d__face coin-3d__face--back"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Text and Countdown Section */}
        <div className="-mt-8 max-w-xl text-center md:mt-0 md:text-left">
          <h1 className="mt-0 text-4xl font-extrabold uppercase leading-tight tracking-tight text-transparent sm:text-5xl lg:text-[4rem] lg:leading-[1.05] bg-gradient-to-r from-[#F2E8FF] via-[#E6CCFF] to-[#CFA6FF] bg-clip-text">
            INVEST IN
        <br/>
 
      
            TOMORROW,

  <br/>
      
            Today
          </h1>

          <p className="mt-6 text-base text-white/70 sm:text-lg">
            Your path to growth, rewards, and community success begins here.
            Join a network built on trust, innovation, and shared opportunity.
          </p>

          {!isExpired && (
            <>
              <h2 className="text-white text-2xl font-semibold mt-2">
                Pre-Sale Time Left
              </h2>

              {/* Countdown Timer */}
              <div className="mt-10 flex flex-nowrap justify-center gap-2.5 overflow-x-auto pb-2 md:flex-wrap md:justify-start md:overflow-visible md:pb-0">
                {countdownUnits.map(({ label, value }) => (
                  <div
                    key={label}
                    className="flex min-w-[4.25rem] flex-shrink-0 flex-col items-center rounded-2xl border border-white/10 bg-white/[0.06] px-3 py-3 text-white shadow-[0_20px_45px_-20px_rgba(104,24,255,0.45)] backdrop-blur-sm sm:min-w-[5.5rem] sm:px-4 sm:py-4"
                  >
                    <span className="text-2xl font-semibold tracking-[0.3em] sm:text-3xl sm:tracking-widest">
                      {formatTimeUnit(value)}
                    </span>
                    <span className="mt-1 text-[0.6rem] uppercase tracking-[0.35em] text-white/60 sm:mt-2 sm:text-xs">
                      {label}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Background Glow */}
        <div className="pointer-events-none absolute inset-y-0 left-1/2 hidden w-[45vw] -translate-x-[10%] rotate-6 rounded-[40%] bg-gradient-to-r from-purple-700/10 via-purple-600/5 to-transparent blur-[160px] md:block" />
      </div>
    </section>
  );
};

export default Hero;
